import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { clientApi, isSetJob, JobListItem } from '@/api/client'
import { qk } from '@/api/queryKeys'
import { getDefaultWatchlistId } from '@/auth/session'
import AskInput from '@/components/AskInput'
import Topbar from '@/components/Topbar'
import ErrorBox from '@/components/ErrorBox'
import Skeleton from '@/components/Skeleton'
import { timeAgo } from '@/utils/format'

function statusPill(status: string) {
  const s = String(status || '').toLowerCase()
  if (s === 'succeeded' || s === 'done') return 'pill ok'
  if (s === 'failed') return 'pill bad'
  return 'pill'
}

function probLabel(v: any): string | null {
  if (typeof v !== 'number') return null
  return `${Math.round(v * 100)}%`
}

type ToastItem = {
  id: string
  tone: 'ok' | 'bad'
  title: string
  body: string
}

export default function MyQuestions() {
  const [filter, setFilter] = useState<'all' | 'running' | 'done' | 'failed'>('all')
  const [retryingJobId, setRetryingJobId] = useState<string | null>(null)
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const queryClient = useQueryClient()

  function pushToast(tone: 'ok' | 'bad', title: string, body: string) {
    const id = `toast_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    setToasts((prev) => [...prev, { id, tone, title, body }].slice(-4))
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 8000)
  }

  // MyQuestions needs a larger page size than Topbar's 30-job badge query,
  // so it uses its own key (different `limit`). React Query dedupes within
  // the same key across components.
  const jobsQuery = useQuery({
    queryKey: qk.jobs.list({ askOnly: true, limit: 50 }),
    queryFn: () => clientApi.jobs(undefined, 50, true),
    refetchInterval: 5_000,
    staleTime: 2_000,
  })
  const jobs = useMemo<JobListItem[]>(
    () => (Array.isArray(jobsQuery.data?.jobs) ? jobsQuery.data!.jobs : []),
    [jobsQuery.data],
  )
  const error = jobsQuery.error
  const loading = jobsQuery.isLoading

  // Compute status-change toasts by diffing against the previous snapshot.
  const prevJobsRef = useRef<Map<string, JobListItem>>(new Map())
  useEffect(() => {
    const prevById = prevJobsRef.current
    for (const job of jobs) {
      const before = prevById.get(job.job_id)
      const beforeStatus = String(before?.status || '')
      const afterStatus = String(job.status || '')
      const becameDone = (beforeStatus === 'queued' || beforeStatus === 'running') && afterStatus === 'succeeded'
      const becameFailed = (beforeStatus === 'queued' || beforeStatus === 'running') && afterStatus === 'failed'
      if (becameDone) {
        if (isSetJob(job)) {
          const generated = job.output_json?.generated
          const succeeded = job.output_json?.succeeded
          const failed = job.output_json?.failed
          const bits = [
            typeof generated === 'number' ? `${generated} generated` : null,
            typeof succeeded === 'number' ? `${succeeded} forecasted` : null,
            typeof failed === 'number' && failed > 0 ? `${failed} failed` : null,
          ].filter(Boolean)
          pushToast('ok', 'Source set complete', `${job.display_question || 'Forecast set'}${bits.length ? ` · ${bits.join(' · ')}` : ''}`)
        } else {
          const bits = [probLabel(job.output_json?.probability), (job.output_json?.summary || '').slice(0, 160)].filter(Boolean)
          pushToast('ok', 'Question forecast complete', `${job.display_question || 'Question'}${bits.length ? ` · ${bits.join(' · ')}` : ''}`)
        }
        // New forecast data may affect the dashboard summary / watchlist —
        // proactively invalidate those queries so the user sees fresh KPIs.
        queryClient.invalidateQueries({ queryKey: qk.watchlists.all() })
      } else if (becameFailed) {
        pushToast('bad', isSetJob(job) ? 'Source set failed' : 'Question forecast failed', `${job.display_question || 'Question'}${job.error ? ` · ${job.error}` : ''}`)
      }
    }
    prevJobsRef.current = new Map(jobs.map((j) => [j.job_id, j]))
  }, [jobs, queryClient])

  const sorted = useMemo(
    () => [...jobs].sort((a, b) => Date.parse(b.created_at || '') - Date.parse(a.created_at || '')),
    [jobs],
  )

  const filtered = useMemo(() => {
    if (filter === 'all') return sorted
    if (filter === 'running') return sorted.filter((j) => j.status === 'queued' || j.status === 'running')
    if (filter === 'done') return sorted.filter((j) => j.status === 'succeeded')
    return sorted.filter((j) => j.status === 'failed')
  }, [sorted, filter])

  const counts = useMemo(() => ({
    all: sorted.length,
    running: sorted.filter((j) => j.status === 'queued' || j.status === 'running').length,
    done: sorted.filter((j) => j.status === 'succeeded').length,
    failed: sorted.filter((j) => j.status === 'failed').length,
  }), [sorted])

  async function retryJob(job: JobListItem) {
    const question = String(job.display_question || '').trim()
    if (!question) return
    setRetryingJobId(job.job_id)
    try {
      await clientApi.askQuestion(question, job.watchlist_id || undefined)
      // Invalidate both the 30-item (Topbar) and 50-item (this page) queries.
      queryClient.invalidateQueries({ queryKey: qk.jobs.all() })
    } catch (e: any) {
      alert(e?.message || 'Failed to retry question')
    } finally {
      setRetryingJobId(null)
    }
  }

  return (
    <div className="container">
      <Topbar subtitle="My Questions" />

      {/* Ad-hoc ask box. This used to live on the standalone Home/Dashboard;
          now that Workspaces is the home, the quick-ask belongs here with the
          rest of the user's question activity. */}
      <AskInput
        watchlistId={getDefaultWatchlistId()}
        onItemCreated={() => queryClient.invalidateQueries({ queryKey: qk.jobs.all() })}
      />

      {toasts.length > 0 && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 40, display: 'grid', gap: 10, width: 'min(380px, calc(100vw - 32px))' }}>
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className="card"
              style={{
                borderLeft: toast.tone === 'ok' ? '3px solid var(--ok)' : '3px solid var(--bad)',
                boxShadow: 'var(--shadow)',
                padding: '12px 14px',
              }}
            >
              <div className="row" style={{ justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800, marginBottom: 4 }}>{toast.title}</div>
                  <div className="muted" style={{ fontSize: 12, lineHeight: 1.5, overflowWrap: 'break-word' }}>{toast.body}</div>
                </div>
                <button className="btn small" type="button" onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}>
                  Close
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {loading && !error && (
        <div className="grid" style={{ marginTop: 16 }}>
          <Skeleton variant="card" count={4} />
        </div>
      )}

      {!loading && !error && (
        <div className="grid">
          <div className="card">
              <h2 style={{ marginTop: 0 }}>Ask Activity</h2>
              <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
              Tracks your recent ad-hoc questions and source-set runs, including ones still running in the background.
              </div>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <button className={`btn ${filter === 'all' ? 'primary' : ''}`} type="button" onClick={() => setFilter('all')}>
                All ({counts.all})
              </button>
              <button className={`btn ${filter === 'running' ? 'primary' : ''}`} type="button" onClick={() => setFilter('running')}>
                Running ({counts.running})
              </button>
              <button className={`btn ${filter === 'done' ? 'primary' : ''}`} type="button" onClick={() => setFilter('done')}>
                Done ({counts.done})
              </button>
              <button className={`btn ${filter === 'failed' ? 'primary' : ''}`} type="button" onClick={() => setFilter('failed')}>
                Failed ({counts.failed})
              </button>
            </div>
            <div className="stack" style={{ gap: 0 }}>
              {filtered.map((job) => (
                <div key={job.job_id} className="signal-row">
                  <div className="row" style={{ justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 700, lineHeight: 1.45 }}>{job.display_question || 'Untitled question'}</div>
                      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                        {isSetJob(job) ? 'From sources' : 'Single question'}
                        {' · '}
                        Asked {job.created_at ? timeAgo(job.created_at) : 'recently'}
                        {job.started_at ? ` · Started ${timeAgo(job.started_at)}` : ''}
                        {job.ended_at ? ` · Finished ${timeAgo(job.ended_at)}` : ''}
                      </div>
                      {job.error && <div className="muted" style={{ fontSize: 12, marginTop: 6, color: 'var(--bad)' }}>{job.error}</div>}
                    </div>
                    <div className="row" style={{ gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <span className={statusPill(job.status)}>{job.status}</span>
                      {isSetJob(job) && typeof job.output_json?.generated === 'number' ? <span className="pill">{job.output_json.generated} generated</span> : null}
                      {isSetJob(job) && typeof job.output_json?.succeeded === 'number' ? <span className="pill">{job.output_json.succeeded} forecasted</span> : null}
                      {probLabel(job.output_json?.probability) && <span className="pill">{probLabel(job.output_json?.probability)}</span>}
                      {job.set_id && <Link className="btn small" to={`/question-sets/${job.set_id}`}>View set</Link>}
                      {job.item_id && (
                        <Link
                          className="btn small"
                          to={`/items/${job.item_id}`}
                          state={{ from: { kind: 'other', title: 'My Questions', path: '/my-questions' } }}
                        >
                          View
                        </Link>
                      )}
                      {job.status === 'failed' && !isSetJob(job) && (
                        <button className="btn small" type="button" onClick={() => retryJob(job)} disabled={retryingJobId === job.job_id}>
                          {retryingJobId === job.job_id ? 'Retrying...' : 'Retry'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {filtered.length === 0 && (
                <div className="muted" style={{ fontSize: 13 }}>No ask activity yet.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {error && <ErrorBox error={error} />}
    </div>
  )
}
